import { Component, EventEmitter, Input, model, OnInit, Output } from '@angular/core';
import { AssetDto } from '../../../../../../../api/src/asset/models/asset.dto';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ImageActionType } from '../../../../../../../api/src/asset/models/image-action.dto';
import { ImageEditorService } from '../../image-editor.service';
import { ImagePickerActionsDto } from '../../../../../../../api/src/creative-unit-package/models/customization/customization.dto';
import { ConnectorUtils } from '../../../../../../../api/src/data-source/connectors/connector.utils';
import { MatDialog } from '@angular/material/dialog';
import { AssetPickerDialogComponent } from '../../../../_core/state/asset/asset-picker-dialog/asset-picker-dialog.component';
import { uploadDataSource } from '../../../../_core/state/data-source/data-source.data';
import { AssetCardLayouts } from '../../../../_core/state/asset/asset.data';

@Component({
	selector: 'app-image-tool-image-generation',
	templateUrl: './image-tool-image-generation.component.html',
	styleUrls: ['./image-tool-image-generation.component.scss']
})
export class ImageToolImageGenerationComponent implements OnInit {
	@Input() public activeAsset: AssetDto;
	@Input() tools: ImageActionType[];
	@Input() config: ImagePickerActionsDto;
	@Output() setActiveAsset: EventEmitter<AssetDto> = new EventEmitter<AssetDto>();
	@Output() setActiveVariations: EventEmitter<Partial<AssetDto>[]> = new EventEmitter<Partial<AssetDto>[]>();
	@Output() setLoadingMessage: EventEmitter<string | null> = new EventEmitter<string | null>();

	public loadingAction: ImageActionType;
	public textToImageForm: UntypedFormGroup;
	public ImageActionType = ImageActionType;

	public supportedInputs = [];
	public aspectRatios = [];

	// Fallback state properties
	public isFallbackMode = false;
	public availableModels: any[] = [];
	public defaultAspectRatios = [
		{ label: '1:1 (Square)', value: '1:1' },
		{ label: '16:9 (Landscape)', value: '16:9' },
		{ label: '9:16 (Portrait)', value: '9:16' },
		{ label: '4:3 (Landscape)', value: '4:3' },
		{ label: '3:4 (Portrait)', value: '3:4' }
	];
	public showAdvancedOptions = false;
	public selectedReferenceImages: AssetDto[] = [];

	constructor(
		public imageEditorService: ImageEditorService,
		private dialog: MatDialog
	) { }

	ngOnInit(): void {
		// Detect fallback state (when no dataSource is configured)
		if (!this.config?.dataSource) {
			console.warn('ImageToolImageGenerationComponent: No dataSource configured, entering fallback mode');
			this.isFallbackMode = true;
			this.initializeFallbackMode();
			return;
		}

		// Original initialization for data source mode
		const classRef = ConnectorUtils.getDefaultConnectorConfig(this.config.dataSource.connectorType);
		this.supportedInputs = classRef.supportedInputs;
		this.aspectRatios = classRef.aspectRatios;
		console.log('supportedInputs', this.supportedInputs);

		this.textToImageForm = new UntypedFormGroup({
			model: new UntypedFormControl(this.config.dataSource.config?.models?.[0]),
			prompt: new UntypedFormControl('')
		});

		if (this.supportedInputs.includes('aspect_ratio')) {
			this.textToImageForm.addControl('aspect_ratio', new UntypedFormControl(this.aspectRatios[0]));
		}
	}

	/**
	 * Initialize component in fallback mode
	 * - Fetch available models from /ai/get-models endpoint
	 * - Initialize form with all text-to-image parameters
	 */
	private initializeFallbackMode(): void {
		// Fetch available models dynamically
		this.imageEditorService.getAvailableModels('text-to-image')
			.subscribe(
				response => {
					this.availableModels = response.models.map(model => ({
						label: model.label,
						value: model.id,
						supportsReferenceImages: model.metadata?.supportsReferenceImages ?? false
					}));

					// Initialize form after models are loaded
					this.initializeFallbackForm();
				},
				error => {
					console.error('Failed to fetch available models:', error);
					// Initialize form anyway with empty models
					this.initializeFallbackForm();
				}
			);
	}

	/**
	 * Initialize form for fallback mode with all text-to-image parameters
	 */
	private initializeFallbackForm(): void {
		this.textToImageForm = new UntypedFormGroup({
			model: new UntypedFormControl(
				this.availableModels.length > 0 ? this.availableModels[0] : null,
				Validators.required
			),
			prompt: new UntypedFormControl('', Validators.required),
			aspectRatio: new UntypedFormControl(this.defaultAspectRatios[0]),
			numberOfResults: new UntypedFormControl(1, [
				Validators.required,
				Validators.min(1),
				Validators.max(4)
			]),
			referenceImages: new UntypedFormControl([]),
			// Advanced settings
			temperature: new UntypedFormControl(1.0, [
				Validators.min(0.0),
				Validators.max(2.0)
			]),
			topP: new UntypedFormControl(1.0, [
				Validators.min(0.0),
				Validators.max(1.0)
			]),
			seed: new UntypedFormControl(null),
			negativePrompt: new UntypedFormControl('')
		});

		// Watch for model changes and clear reference images if new model doesn't support them
		this.textToImageForm.get('model').valueChanges.subscribe(selectedModel => {
			if (selectedModel && !selectedModel.supportsReferenceImages && this.selectedReferenceImages.length > 0) {
				console.log('Clearing reference images - selected model does not support them');
				this.selectedReferenceImages = [];
				this.textToImageForm.patchValue({ referenceImages: [] });
			}
		});
	}

	replaceBackground() {
		// this.setActiveVariations.emit(
		// 	[{"assetPath":"https://bria-temp.s3.amazonaws.com/api/replace_bg_results/99266e6b64959956_A_bright_sunny_sky_0ce63d22-d786-11ee-aa8a-8e7556797829/seed_675652962.jpg?AWSAccessKeyId=ASIAUL5JH7ABLVJHCBA2&Signature=iAKwGCfYnouYhgDVOLYnl6%2F%2FIPg%3D&x-amz-security-token=FwoGZXIvYXdzED0aDINlcRVi17L3xmtRRCKYBPVm5k%2F7AjMwy%2F3wSMDLx8bsIHyfjH%2FeRUD%2Fb9BWDsd6OtAjRNXSbT5occd7%2FZIQOy5qgHdZayS%2FSd3E5sdQD5gqqr99L7Aa3geXIbvkDjOkFp2NeD%2BMy8TlipsIwVFw8dGq4HUJQ4IvuLuj4pamqt3kgUPo1rhbJyrOCxkGBmbXNvXi8l4wuYtPqudJhXDeHkJOnlPXMGWAUXszQwElpjDJMyLz9z6%2F5K9QTOKjRGyEMNt0RRWZB1yhCd2TBysimmBxw5hNaHf44WFrTZXrGbqaD3Nn6zzsyMVpvIZv0Si6zBs0kpfbhxKxOTOoWByeua%2BF%2BB7ffVjVVWl7Ux8vM8AU1nJ7vpssS46yshiRLm%2BZ36%2FQrrxTbf79rz4GYjdZPtZsWc9GWOmUMoE3aTS%2B6hcKP3bKN2C22OXuTRYdQnhwWJgm%2FS8c3rZeevXLpts5dQDgq7YwJ%2FWKOo0BgzuwpyKrsWgqioobp2HvGK6e%2Bpwb6bqnUhntI98ocShTJozVX8fxmH5EMPfSz6UgtF4qg6TObwugsDuSoNCMRQdgH1j7o0kf%2Fci2imD6SdNg3TneTUmZDW%2BaMahAHHFmFTPvWWVOZquYLh46zsbjtUlTrpDYL9yJC5hb%2FgEYe9%2BpS%2FQy9tSwESrg7Gimb2%2BOSSRrSWDhzDeXcz41DHuE1WDlaaWlzIfaqGzvSzWxD%2FEuXfKcX2UKyAaLBThlKKilha8GMir5U8wVnFiSJcVt%2BTLfWGiLrF8uof5Z1rIq3u7uH8A6qXFSoFZ%2F7U2jHdM%3D&Expires=1709872923","externalIds":{"bria_visual_id":"99266e6b64959956"}},{"assetPath":"https://bria-temp.s3.amazonaws.com/api/replace_bg_results/99266e6b64959956_A_bright_sunny_sky_0ce63d22-d786-11ee-aa8a-8e7556797829/seed_675652963.jpg?AWSAccessKeyId=ASIAUL5JH7ABLVJHCBA2&Signature=nBFXCUK8Snw9h0P27Bxo7bDauHo%3D&x-amz-security-token=FwoGZXIvYXdzED0aDINlcRVi17L3xmtRRCKYBPVm5k%2F7AjMwy%2F3wSMDLx8bsIHyfjH%2FeRUD%2Fb9BWDsd6OtAjRNXSbT5occd7%2FZIQOy5qgHdZayS%2FSd3E5sdQD5gqqr99L7Aa3geXIbvkDjOkFp2NeD%2BMy8TlipsIwVFw8dGq4HUJQ4IvuLuj4pamqt3kgUPo1rhbJyrOCxkGBmbXNvXi8l4wuYtPqudJhXDeHkJOnlPXMGWAUXszQwElpjDJMyLz9z6%2F5K9QTOKjRGyEMNt0RRWZB1yhCd2TBysimmBxw5hNaHf44WFrTZXrGbqaD3Nn6zzsyMVpvIZv0Si6zBs0kpfbhxKxOTOoWByeua%2BF%2BB7ffVjVVWl7Ux8vM8AU1nJ7vpssS46yshiRLm%2BZ36%2FQrrxTbf79rz4GYjdZPtZsWc9GWOmUMoE3aTS%2B6hcKP3bKN2C22OXuTRYdQnhwWJgm%2FS8c3rZeevXLpts5dQDgq7YwJ%2FWKOo0BgzuwpyKrsWgqioobp2HvGK6e%2Bpwb6bqnUhntI98ocShTJozVX8fxmH5EMPfSz6UgtF4qg6TObwugsDuSoNCMRQdgH1j7o0kf%2Fci2imD6SdNg3TneTUmZDW%2BaMahAHHFmFTPvWWVOZquYLh46zsbjtUlTrpDYL9yJC5hb%2FgEYe9%2BpS%2FQy9tSwESrg7Gimb2%2BOSSRrSWDhzDeXcz41DHuE1WDlaaWlzIfaqGzvSzWxD%2FEuXfKcX2UKyAaLBThlKKilha8GMir5U8wVnFiSJcVt%2BTLfWGiLrF8uof5Z1rIq3u7uH8A6qXFSoFZ%2F7U2jHdM%3D&Expires=1709872923","externalIds":{"bria_visual_id":"99266e6b64959956"}},{"assetPath":"https://bria-temp.s3.amazonaws.com/api/replace_bg_results/99266e6b64959956_A_bright_sunny_sky_0ce63d22-d786-11ee-aa8a-8e7556797829/seed_675652964.jpg?AWSAccessKeyId=ASIAUL5JH7ABLVJHCBA2&Signature=HiaH%2F2IXXD6Z%2Fpbsuzf10LQHMR8%3D&x-amz-security-token=FwoGZXIvYXdzED0aDINlcRVi17L3xmtRRCKYBPVm5k%2F7AjMwy%2F3wSMDLx8bsIHyfjH%2FeRUD%2Fb9BWDsd6OtAjRNXSbT5occd7%2FZIQOy5qgHdZayS%2FSd3E5sdQD5gqqr99L7Aa3geXIbvkDjOkFp2NeD%2BMy8TlipsIwVFw8dGq4HUJQ4IvuLuj4pamqt3kgUPo1rhbJyrOCxkGBmbXNvXi8l4wuYtPqudJhXDeHkJOnlPXMGWAUXszQwElpjDJMyLz9z6%2F5K9QTOKjRGyEMNt0RRWZB1yhCd2TBysimmBxw5hNaHf44WFrTZXrGbqaD3Nn6zzsyMVpvIZv0Si6zBs0kpfbhxKxOTOoWByeua%2BF%2BB7ffVjVVWl7Ux8vM8AU1nJ7vpssS46yshiRLm%2BZ36%2FQrrxTbf79rz4GYjdZPtZsWc9GWOmUMoE3aTS%2B6hcKP3bKN2C22OXuTRYdQnhwWJgm%2FS8c3rZeevXLpts5dQDgq7YwJ%2FWKOo0BgzuwpyKrsWgqioobp2HvGK6e%2Bpwb6bqnUhntI98ocShTJozVX8fxmH5EMPfSz6UgtF4qg6TObwugsDuSoNCMRQdgH1j7o0kf%2Fci2imD6SdNg3TneTUmZDW%2BaMahAHHFmFTPvWWVOZquYLh46zsbjtUlTrpDYL9yJC5hb%2FgEYe9%2BpS%2FQy9tSwESrg7Gimb2%2BOSSRrSWDhzDeXcz41DHuE1WDlaaWlzIfaqGzvSzWxD%2FEuXfKcX2UKyAaLBThlKKilha8GMir5U8wVnFiSJcVt%2BTLfWGiLrF8uof5Z1rIq3u7uH8A6qXFSoFZ%2F7U2jHdM%3D&Expires=1709872923","externalIds":{"bria_visual_id":"99266e6b64959956"}},{"assetPath":"https://bria-temp.s3.amazonaws.com/api/replace_bg_results/99266e6b64959956_A_bright_sunny_sky_0ce63d22-d786-11ee-aa8a-8e7556797829/seed_675652965.jpg?AWSAccessKeyId=ASIAUL5JH7ABLVJHCBA2&Signature=sICmm1Bv%2BIZ2WRTHQHsoF68i%2B94%3D&x-amz-security-token=FwoGZXIvYXdzED0aDINlcRVi17L3xmtRRCKYBPVm5k%2F7AjMwy%2F3wSMDLx8bsIHyfjH%2FeRUD%2Fb9BWDsd6OtAjRNXSbT5occd7%2FZIQOy5qgHdZayS%2FSd3E5sdQD5gqqr99L7Aa3geXIbvkDjOkFp2NeD%2BMy8TlipsIwVFw8dGq4HUJQ4IvuLuj4pamqt3kgUPo1rhbJyrOCxkGBmbXNvXi8l4wuYtPqudJhXDeHkJOnlPXMGWAUXszQwElpjDJMyLz9z6%2F5K9QTOKjRGyEMNt0RRWZB1yhCd2TBysimmBxw5hNaHf44WFrTZXrGbqaD3Nn6zzsyMVpvIZv0Si6zBs0kpfbhxKxOTOoWByeua%2BF%2BB7ffVjVVWl7Ux8vM8AU1nJ7vpssS46yshiRLm%2BZ36%2FQrrxTbf79rz4GYjdZPtZsWc9GWOmUMoE3aTS%2B6hcKP3bKN2C22OXuTRYdQnhwWJgm%2FS8c3rZeevXLpts5dQDgq7YwJ%2FWKOo0BgzuwpyKrsWgqioobp2HvGK6e%2Bpwb6bqnUhntI98ocShTJozVX8fxmH5EMPfSz6UgtF4qg6TObwugsDuSoNCMRQdgH1j7o0kf%2Fci2imD6SdNg3TneTUmZDW%2BaMahAHHFmFTPvWWVOZquYLh46zsbjtUlTrpDYL9yJC5hb%2FgEYe9%2BpS%2FQy9tSwESrg7Gimb2%2BOSSRrSWDhzDeXcz41DHuE1WDlaaWlzIfaqGzvSzWxD%2FEuXfKcX2UKyAaLBThlKKilha8GMir5U8wVnFiSJcVt%2BTLfWGiLrF8uof5Z1rIq3u7uH8A6qXFSoFZ%2F7U2jHdM%3D&Expires=1709872923","externalIds":{"bria_visual_id":"99266e6b64959956"}}]
		// );
		// return;

		if (this.isFallbackMode) {
			this.generateWithFallback();
			return;
		}

		// Original data source mode logic
		this.loadingAction = ImageActionType.TextToImage;

		let params = {
			...this.textToImageForm.value,
			model: this.textToImageForm.value.model?.value || null
		}

		this.imageEditorService.generate(
			this.config.dataSource.slug,
			params
		).subscribe(assets => {
			this.loadingAction = null;
			this.setActiveVariations.emit(assets.map(asset => ({ ...asset, title: this.textToImageForm.value.prompt })));
		}, error => {
			this.loadingAction = null;
			console.error('Error with text to image', error);
		});
	}

	/**
	 * Generate images in fallback mode using the new /ai/text-to-image endpoint
	 */
	private generateWithFallback(): void {
		if (this.textToImageForm.invalid) {
			console.warn('Form is invalid', this.textToImageForm.errors);
			return;
		}

		this.loadingAction = ImageActionType.TextToImage;
		this.setLoadingMessage.emit('Generating your images...');

		const formValue = this.textToImageForm.value;
		const request: any = {
			prompt: formValue.prompt,
			model: formValue.model?.value || formValue.model,
			aspectRatio: formValue.aspectRatio?.value || formValue.aspectRatio,
			numberOfResults: formValue.numberOfResults,
			referenceImages: formValue.referenceImages || []
		};

		// Include advanced settings if they have values
		if (formValue.temperature != null && formValue.temperature !== 1.0) {
			request.temperature = formValue.temperature;
		}
		if (formValue.topP != null && formValue.topP !== 1.0) {
			request.topP = formValue.topP;
		}
		if (formValue.seed != null) {
			request.seed = formValue.seed;
		}
		if (formValue.negativePrompt) {
			request.negativePrompt = formValue.negativePrompt;
		}

		this.imageEditorService.generateTextToImage(request)
			.subscribe(
				response => {
					this.loadingAction = null;
					this.setLoadingMessage.emit(null);
					this.setActiveVariations.emit(response.assets);
				},
				error => {
					this.loadingAction = null;
					this.setLoadingMessage.emit(null);
					console.error('Error generating images in fallback mode:', error);
					// TODO: Display user-friendly error message
				}
			);
	}

	/**
	 * Open asset picker to select reference images (1-14 images)
	 */
	async openReferenceImagePicker(): Promise<void> {
		// Check if we've already reached the max limit
		if (this.selectedReferenceImages.length >= 14) {
			console.warn('Maximum 14 reference images allowed');
			return;
		}

		const dialogRef = this.dialog.open(AssetPickerDialogComponent, {
			data: {
				config: {
					datasource: { ...uploadDataSource },
					mediaType: 'image',
					ui: {
						layout: AssetCardLayouts.find(layout => layout.id === 'grid-6'),
						showUpload: true
					},
					// Limit selection to remaining slots (max 14 total)
					maxSelection: 14 - this.selectedReferenceImages.length
				}
			},
			panelClass: ['fullscreen', 'min-height', 'stretch', 'stretch-height']
		});

		const selectedAssets = await dialogRef.afterClosed().toPromise();
		
		if (selectedAssets && selectedAssets.length > 0) {
			// Add new assets to selection, ensuring we don't exceed 14 total
			const availableSlots = 14 - this.selectedReferenceImages.length;
			const assetsToAdd = selectedAssets.slice(0, availableSlots);
			
			this.selectedReferenceImages = [...this.selectedReferenceImages, ...assetsToAdd];
			
			// Update form control with asset IDs
			const assetIds = this.selectedReferenceImages.map(asset => parseInt(asset.id, 10));
			this.textToImageForm.patchValue({ referenceImages: assetIds });
		}
	}

	/**
	 * Remove a reference image by index
	 */
	removeReferenceImage(index: number): void {
		this.selectedReferenceImages = this.selectedReferenceImages.filter((_, i) => i !== index);
		
		// Update form control
		const assetIds = this.selectedReferenceImages.map(asset => parseInt(asset.id, 10));
		this.textToImageForm.patchValue({ referenceImages: assetIds });
	}

	/**
	 * Check if max reference images (14) are selected
	 */
	get isMaxReferenceImagesSelected(): boolean {
		return this.selectedReferenceImages.length >= 14;
	}

	/**
	 * Check if the currently selected model supports reference images
	 */
	get selectedModelSupportsReferenceImages(): boolean {
		const selectedModel = this.textToImageForm?.value?.model;
		return selectedModel?.supportsReferenceImages ?? false;
	}

}
